import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import { filter } from 'rxjs';

import { TenantState } from '@core/models/tenant.state';

import { environment } from '../../../environments/environment';

/**
 * Ambient tenant scope (HLD §4 SM-02). `marketplace` scope shows every
 * participating tenant; a tenant scope filters catalog queries and swaps the
 * shell branding.
 *
 * The scope is entered two ways, and they behave differently:
 *
 * - **Path scope** - the `/retailer/:tenantSlug` resolver enters it per visit,
 *   and any navigation away drops it. The shopper is on the marketplace and
 *   passing through a retailer's page.
 * - **Host scope** - `TenantHostService` enters it once at bootstrap when the
 *   request arrived on a tenant's own subdomain. That scope is **sticky**: the
 *   whole origin belongs to that tenant, so browsing to the cart or a listing
 *   must not strip the branding.
 */
@Injectable({ providedIn: 'root' })
export class TenantScopeService {
  private readonly _tenant = signal<TenantState | null>(null);
  private readonly _isHostScope = signal(false);

  /** The active storefront tenant, or null in marketplace scope. */
  readonly tenant = this._tenant.asReadonly();
  readonly isTenantScope = computed(() => this._tenant() !== null);
  /** True when the scope came from the request host, not from a route. */
  readonly isHostScope = this._isHostScope.asReadonly();

  /**
   * Where "Back to Brijj Finds" has to point.
   *
   * Under host scope the tenant owns this origin and `/` is their storefront,
   * so leaving for the marketplace is a cross-origin trip and needs an
   * absolute href. Under path scope `/` is already the marketplace home, so
   * this stays null and the link routes client-side instead of reloading.
   */
  readonly marketplaceHomeUrl = computed(() => (this._isHostScope() ? environment.marketplaceWebUrl : null));

  constructor() {
    // Auto-exit: any navigation that leaves `/retailer/…` (logo, back-band,
    // cart, favorites, …) drops the storefront scope; the storefront
    // resolver re-enters it on the next `/retailer/:tenantSlug` visit.
    inject(Router)
      .events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(event => {
        if (!event.urlAfterRedirects.startsWith('/retailer/')) this.exitTenant();
      });
  }

  enterTenant(tenant: TenantState): void {
    this._tenant.set(tenant);
  }

  /** Claims the whole origin for a tenant (SM-02). Cannot be exited by routing. */
  enterHostTenant(tenant: TenantState): void {
    this._tenant.set(tenant);
    this._isHostScope.set(true);
  }

  /** No-op under host scope - the origin belongs to the tenant either way. */
  exitTenant(): void {
    if (this._isHostScope()) return;
    this._tenant.set(null);
  }
}
